import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLeadNotifications, markLeadNotificationsSeen } from "@/lib/apiClient";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, BellRing, UserPlus, X, ArrowRight } from "lucide-react";

const POLL_MS = 10000;
const Ctx = createContext(null);
const kindLabel = (n) => (n.kind === "repeat" ? "Bestaande lead vulde opnieuw een formulier in" : "Nieuwe websitelead");

function chime() {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0], [1320, 0.18]].forEach(([f, t]) => {
      const o = ac.createOscillator(), g = ac.createGain();
      o.frequency.value = f; o.type = "sine";
      g.gain.setValueAtTime(0.0001, ac.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.25, ac.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + t + 0.35);
      o.connect(g).connect(ac.destination); o.start(ac.currentTime + t); o.stop(ac.currentTime + t + 0.4);
    });
    setTimeout(() => ac.close(), 1000);
  } catch { /* audio not available */ }
}

export function NewLeadsProvider({ children }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [popups, setPopups] = useState([]);
  const known = useRef(null);
  const navigate = useNavigate();

  const open = useCallback((n) => { setPopups((p) => p.filter((x) => x.id !== n.id)); navigate(`/belomgeving/${n.lead_id}`); }, [navigate]);

  useEffect(() => {
    let alive = true;
    const tick = () => getLeadNotifications().then((d) => {
      if (!alive) return;
      setData(d);
      if (known.current === null) { known.current = new Set(d.items.map((n) => n.id)); return; }
      const fresh = d.items.filter((n) => !known.current.has(n.id) && n.created_at > d.seen_at);
      d.items.forEach((n) => known.current.add(n.id));
      if (!fresh.length) return;
      setPopups((p) => [...fresh, ...p].slice(0, 4));
      chime();
      if ("Notification" in window && Notification.permission === "granted") {
        fresh.forEach((n) => {
          const bn = new Notification(`${kindLabel(n)}: ${n.name}`, { body: [n.form_name, n.city, n.campaign].filter(Boolean).join(" · "), tag: n.id });
          bn.onclick = () => { window.focus(); open(n); bn.close(); };
        });
      }
    }).catch(() => {});
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [open]);

  const markSeen = () => { markLeadNotificationsSeen().catch(() => {}); setData((d) => ({ ...d, unread: 0, seen_at: new Date().toISOString() })); };
  const dismiss = (id) => setPopups((p) => p.filter((x) => x.id !== id));

  return (
    <Ctx.Provider value={{ ...data, markSeen, open }}>
      {children}
      <NewLeadPopups popups={popups} dismiss={dismiss} open={open} />
    </Ctx.Provider>
  );
}

function NewLeadPopups({ popups, dismiss, open }) {
  if (!popups.length) return null;
  return (
    <div className="fixed bottom-24 lg:bottom-6 right-4 z-50 space-y-3 w-[340px] max-w-[calc(100vw-2rem)]" data-testid="new-lead-popups">
      {popups.map((n) => (
        <div key={n.id} data-testid={`new-lead-popup-${n.id}`} className="rounded-2xl bg-white border-2 border-[#16BFBF] p-4 shadow-2xl fe-fade-up">
          <div className="flex items-start gap-3">
            <span className="relative h-10 w-10 rounded-xl bg-[#16BFBF] text-white flex items-center justify-center shrink-0">
              <span className="absolute inset-0 rounded-xl bg-[#16BFBF] animate-ping opacity-30" />
              <UserPlus className="h-5 w-5 relative" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-[#0C6E6E]">{kindLabel(n)}</p>
              <p className="font-heading font-semibold text-[#16202B] truncate" data-testid={`new-lead-popup-name-${n.id}`}>{n.name}</p>
              <p className="text-xs text-[#5B6675] truncate">{[n.form_name, n.city, n.campaign].filter(Boolean).join(" · ")}</p>
            </div>
            <button data-testid={`new-lead-popup-dismiss-${n.id}`} onClick={() => dismiss(n.id)} className="ml-auto p-1 rounded-lg hover:bg-[#EDF1F5] text-[#5B6675]"><X className="h-4 w-4" /></button>
          </div>
          <button data-testid={`new-lead-popup-open-${n.id}`} onClick={() => open(n)}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#001C51] text-white text-sm font-semibold hover:bg-[#0A337A] transition-colors">
            Open in Belomgeving <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function NewLeadBell({ className = "" }) {
  const ctx = useContext(Ctx);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  if (!ctx) return null;
  const { items, unread, markSeen, open, seen_at } = ctx;
  const ask = () => Notification.requestPermission().then(setPerm);
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) markSeen(); }}>
      <PopoverTrigger asChild>
        <button data-testid="new-lead-bell" className={`relative p-2 rounded-xl text-[#001C51] hover:bg-[#EDF1F5] transition-colors ${className}`} aria-label="Nieuwe leads">
          {unread ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
          {unread > 0 && <span data-testid="new-lead-bell-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#16BFBF] text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-0 rounded-2xl" data-testid="new-lead-panel">
        <div className="px-4 py-3 border-b border-[#E2E7EC]"><p className="font-heading font-semibold text-[#16202B]">Websiteleads</p></div>
        {perm === "default" && (
          <button data-testid="enable-browser-notifications" onClick={ask} className="w-full text-left px-4 py-2.5 text-xs font-medium text-[#0C6E6E] bg-[#EAFBFB] hover:bg-[#D6F3F3]">Browsermeldingen aanzetten (ook als dit tabblad op de achtergrond staat)</button>
        )}
        {perm === "denied" && <p className="px-4 py-2 text-xs text-[#B0413E] bg-[#F3DEDD]">Browsermeldingen zijn geblokkeerd in je browserinstellingen.</p>}
        <div className="max-h-80 overflow-y-auto fe-scroll">
          {items.length === 0 ? <p className="px-4 py-6 text-sm text-[#5B6675]" data-testid="new-lead-empty">Nog geen websiteleads binnengekomen.</p>
            : items.map((n) => (
              <button key={n.id} data-testid={`new-lead-item-${n.id}`} onClick={() => open(n)} className="w-full text-left px-4 py-3 border-b border-[#EDF1F5] hover:bg-[#F6F9FB] flex gap-3">
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.created_at > seen_at ? "bg-[#16BFBF]" : "bg-transparent"}`} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#16202B] truncate">{n.name}</span>
                  <span className="block text-xs text-[#5B6675] truncate">{kindLabel(n)} · {new Date(n.created_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}</span>
                </span>
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
